import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';

import {
  sortSelector,
  seoStateSelector,
  selectedDestinationSelector,
  departureSelector,
  arrivalSelector,
  tripDurationSelector,
  filterSelector,
  filteredAndSortedDisplayOnwardDestinations,
  isOnwardFilterAppliedSelector,
  flightSearchDataSelector
} from '@stencil/flights-seo-reducers/src/SRP/redux/selectors';
import {
  updateJourneyType,
  sortDestinations,
  updateSelectedDestination,
  updateSelectedFlight,
  updateDeparture,
  updateArrival,
  applyFilter
} from '@stencil/flights-seo-reducers/src/SRP/redux/actions';
import { defaultCityCountryFilterSettings } from '@stencil/flights-seo-reducers/src/SRP/redux/reducers';
import { getFareData } from '@ibibo/get-fare-calendar-data';
import addDays from '@stencil/date-utils/src/addDays';
import NoDestinationFound from '@stencil/dstories/src/components/CityCountryPage/Common/no-destination-found';
import SearchWidget from '../search-widget/search-widget';
import SeoCityCountryLeft from './seo-city-country-left';
import SeoCityCountryRight from './seo-city-country-right';
import {
  gaPageViewThunk,
  gaPushPageInfoThunk,
  gaGenericEventThunk
} from '@stencil/flights-ga-thunks';

const SeoCityCountry = () => {
  const seoState = useSelector(seoStateSelector);
  const {
    flavour,
    source,
    destination,
    cityCountryDest,
    sourceLinkedData,
    fromSourceCityLinks,
    toSourceCityLinks,
    withinCountryCityLinks,
    topCountriesLinkedData,
    faqs,
    meta_title,
    meta,
    metaLinks,
    h_1_header,
    breadcrumb,
    language,
    travelType,
    journeyType
  } = seoState;

  const dispatch = useDispatch();

  const sortSettings = useSelector(sortSelector);
  const filteredAndSortedDestinations = useSelector(filteredAndSortedDisplayOnwardDestinations);
  const selectedDestination = useSelector(selectedDestinationSelector);
  const departure = useSelector(departureSelector);
  const arrival = useSelector(arrivalSelector);
  const tripDuration = useSelector(tripDurationSelector);
  const currentFilterSettings = useSelector(filterSelector);
  const isFilterApplied = useSelector(isOnwardFilterAppliedSelector);

  const [showDtlSection, setDtlSection] = useState(null);
  const [fareCalendarData, setFareCalendarData] = useState(null);
  const [filterSettings, setFilterSettings] = useState(currentFilterSettings);
  const [isVisibleCalendar, setVisibleCalendar] = useState(false);

  const isRoundTrip = journeyType === 'rt' ? true : false;
  const srcName = source?.name || '';
  const destName = destination?.name || '';

  useEffect(() => {
    dispatch(updateJourneyType('rt'));
    const flavourWeb = flavour === 'mobile' ? 'mWeb' : 'dWeb';
    dispatch(gaPushPageInfoThunk('SRPSeo', flightSearchDataSelector, flavourWeb));
    dispatch(gaPageViewThunk('FlightsSRPSeo', flightSearchDataSelector));
    gaGenericEventThunk('Seo_Flight_City_Country_Page');
  }, []);

  const handleOpenDtlSection = val => {
    setDtlSection(val);
  };

  useEffect(() => {
    if (!selectedDestination || !selectedDestination.toCity) {
      return;
    }
    fetchFareCalendareData('return');
  }, [selectedDestination]);

  const handleCloseDtlSection = () => {
    setDtlSection(null);
    dispatch(updateSelectedDestination({}));
  };

  const applySortSettings = (sortType, isAscending = true) => {
    dispatch(sortDestinations({ currentSort: sortType, isAscending: isAscending }));
  };

  const handleFlightSelection = flight => {
    dispatch(updateSelectedFlight(flight));
  };

  const fetchFareCalendareData = async mode => {
    const fareData: { data?: Object; stats?: Object } = await getFareData(mode, {
      source,
      destination: { iata: selectedDestination?.toCity },
      travelType,
      journeyType,
      departure,
      arrival
    });
    setFareCalendarData(fareData);
  };

  const calendarDateChanged = dateObj => {
    dispatch(updateDeparture(dateObj));
    if (tripDuration && journeyType == 'rt') {
      const arr = addDays(dateObj, tripDuration);
      dispatch(updateArrival(arr));
    }
    setVisibleCalendar(false);
  };

  const handleResetFilters = () => {
    setFilterSettings(defaultCityCountryFilterSettings);
  };

  useEffect(() => {
    dispatch(applyFilter(filterSettings));
  }, [filterSettings]);

  const handleResetIndividualFilter = type => {
    let newFilterSettings = { ...filterSettings };
    if (type instanceof Array) {
      type.forEach(t => {
        newFilterSettings = { ...newFilterSettings, [t]: defaultCityCountryFilterSettings[t] };
      });
    } else {
      newFilterSettings = { ...filterSettings, [type]: defaultCityCountryFilterSettings[type] };
    }
    setFilterSettings(newFilterSettings);
  };

  const updateFilter = (filterType, value) => {
    handleCloseDtlSection();
    if (
      filterType.toLowerCase().includes('price') ||
      filterType.toLowerCase().includes('duration')
    ) {
      setFilterSettings(filter => ({
        ...filter,
        [filterType]: { ...filter[filterType], values: [value] }
      }));
      return;
    }
    setFilterSettings(filter => {
      const index = filter[filterType].values.indexOf(value);
      return {
        ...filter,
        [filterType]: {
          ...filter[filterType],
          values:
            index > -1
              ? [
                  ...filter[filterType].values.slice(0, index),
                  ...filter[filterType].values.slice(index + 1)
                ]
              : [...filter[filterType].values, value]
        }
      };
    });
  };

  return (
    <>
      <Helmet title={meta_title} meta={meta} link={metaLinks} />
      <SearchWidget language={language} isCityCountry={true} />
      {filteredAndSortedDestinations && filteredAndSortedDestinations.length === 0 ? (
        !isFilterApplied && <NoDestinationFound source={source} destination={destination} />
      ) : (
        <>
          <SeoCityCountryLeft
            filterSettings={filterSettings}
            isRoundTrip={isRoundTrip}
            isFilterApplied={isFilterApplied}
            updateFilter={updateFilter}
            handleResetIndividualFilter={handleResetIndividualFilter}
            handleResetFilters={handleResetFilters}
          />
          <SeoCityCountryRight
            airportInfo={cityCountryDest.cityCountryAirports}
            selectedDestination={selectedDestination}
            h1header={h_1_header}
            flavour={flavour}
            travelType={travelType}
            source={source}
            destinations={filteredAndSortedDestinations}
            journeyType={journeyType}
            cityRef={cityCountryDest.toCityRef}
            countryRef={cityCountryDest.toCountryRef}
            continentRef={cityCountryDest.toContinentRef}
            showDtlSection={showDtlSection}
            sortSettings={sortSettings}
            isFilterApplied={isFilterApplied}
            handleOpenDtlSection={handleOpenDtlSection}
            handleCloseDtlSection={handleCloseDtlSection}
            handleFlightSelection={handleFlightSelection}
            handleResetFilters={handleResetFilters}
            applySortSettings={applySortSettings}
            countryTravelGuidelines={cityCountryDest.toCountryTravelGuidelines}
            isSEO={true}
            getFareData={fetchFareCalendareData}
            calendarDateChanged={calendarDateChanged}
            fareCalendarData={fareCalendarData}
            isVisibleCalendar={isVisibleCalendar}
            setVisibleCalendar={setVisibleCalendar}
            seoData={{
              fromSourceCityLinks,
              topCountriesLinkedData,
              sourceLinkedData,
              withinCountryCityLinks,
              toSourceCityLinks,
              faqs,
              srcName,
              destName,
              breadcrumb,
              aboutCity: source && source.about_city,
              aboutCountry: destination && destination.gi_description
            }}
          />
        </>
      )}
    </>
  );
};

export default SeoCityCountry;
